/**
 * Guard logic for the loop-proof diagnostics tap gesture (#5105, fixed #5328).
 *
 * `DiagOverlay` (DiagPanel.tsx) arms a document-level "5 rapid taps" gesture so
 * the diagnostics panel is reachable on the bare native loader/strobe BEFORE the
 * app shell mounts, where no menu exists. As originally shipped it counted ANY
 * pointerdown — so hammering a real button (e.g. the Brand Kit "Randomize",
 * which is built to be clicked repeatedly) opened the internal debug overlay
 * (#5328). Two pure guards fix that without losing the escape hatch:
 *
 *  1. `isInteractiveTapTarget` — taps landing on (or inside) an interactive
 *     control never count. Rolling Randomize, tabbing through pickers, or any
 *     ordinary clicking of real UI can't accumulate toward the gesture.
 *  2. `trackDiagTap` — the taps must be STATIONARY: each within `TAP_RADIUS_PX`
 *     of the previous one. Scattered fast clicking around the app resets the run.
 *
 * The gesture therefore only fires on 5 deliberate rapid taps on one
 * non-interactive spot — exactly the #5105 escape hatch. Opening it ON PURPOSE:
 *  - native loader/strobe (pre-shell): tap the same blank spot 5× within 1.5s;
 *  - anywhere in the app shell: 5 rapid taps on one blank/non-interactive spot
 *    (a margin, the loader), or ProfileModal → `openDiagPanel()` (which
 *    dispatches the `xavior:open-diag` window event).
 *
 * Kept pure/DOM-light so the guard is unit-testable (diag-gesture.test.ts).
 */

/** Taps must all land within this rolling window to count as one gesture. */
export const TAP_WINDOW_MS = 1500;

/** Number of qualifying taps that opens the panel. */
export const TAP_COUNT = 5;

/**
 * Max distance (px) from the PREVIOUS tap for a tap to continue the run —
 * roughly a fingertip of wobble. Anything farther is normal UI clicking and
 * restarts the count at the new spot.
 */
export const TAP_RADIUS_PX = 48;

/**
 * Anything a user legitimately clicks in normal use. `[tabindex="-1"]` is
 * excluded — that marks programmatic focus targets (modal containers, live
 * regions), not user-interactive controls, and matching it would let a
 * full-screen wrapper swallow the loader escape hatch.
 */
const INTERACTIVE_SELECTOR = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'option',
  'label',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="combobox"]',
  '[contenteditable="true"]',
  '[contenteditable=""]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** True when a pointerdown target is (or is inside) an interactive control. */
export function isInteractiveTapTarget(target: unknown): boolean {
  const el = target as Element | null;
  if (!el || typeof el.closest !== 'function') return false;
  try {
    return el.closest(INTERACTIVE_SELECTOR) !== null;
  } catch {
    // A malformed selector or detached-node edge must never break the guard —
    // fail open (count the tap) rather than throw in a document listener.
    return false;
  }
}

/** One qualifying tap: epoch ms + viewport coordinates. */
export interface DiagTap {
  t: number;
  x: number;
  y: number;
}

/**
 * Pure tap-run reducer: fold the next qualifying tap into the run. Drops taps
 * older than `TAP_WINDOW_MS`, restarts the run when the new tap strays more
 * than `TAP_RADIUS_PX` from the previous one, and reports `fire: true` (with a
 * cleared run) when `TAP_COUNT` stationary taps land inside the window.
 */
export function trackDiagTap(
  prev: readonly DiagTap[],
  tap: DiagTap,
): { taps: DiagTap[]; fire: boolean } {
  const recent = prev.filter((p) => tap.t - p.t <= TAP_WINDOW_MS);
  const last = recent[recent.length - 1];
  const stationary =
    !last || Math.hypot(tap.x - last.x, tap.y - last.y) <= TAP_RADIUS_PX;
  const taps = stationary ? [...recent, tap] : [tap];
  if (taps.length >= TAP_COUNT) return { taps: [], fire: true };
  return { taps, fire: false };
}
